import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Trophy, ArrowRight } from "lucide-react";

const displayStack = { fontFamily: "'Outfit', system-ui, sans-serif" };

/**
 * Auto-scrolling horizontal marquee of latest Wall of Fame photos.
 * Shows 8-10 photos and a "View all" link to /wall-of-fame.
 * Hidden when there are no entries (no awkward empty section).
 */
export default function WallOfFameMarquee() {
  const [items, setItems] = useState([]);

  useEffect(() => {
    api.get("/wall-of-fame", { params: { limit: 12 } })
      .then(({ data }) => setItems(data.items || []))
      .catch(() => setItems([]));
  }, []);

  if (items.length === 0) return null;

  // Duplicate the list so the marquee loop is seamless
  const loop = [...items, ...items];

  return (
    <section className="relative py-14 sm:py-16 border-y border-white/10 bg-[#080808] overflow-hidden" data-testid="wof-marquee">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(255,0,102,0.08)_0%,_transparent_70%)]" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-end justify-between gap-3 mb-6">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#FF0066]/15 border border-[#FF0066]/30 text-[#FF0066] text-[11px] uppercase tracking-wider font-bold">
              <Trophy className="w-3.5 h-3.5" /> Wall of Fame
            </div>
            <h2 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-bold" style={displayStack}>
              Real students. Real wins.
            </h2>
            <p className="text-sm text-white/60 mt-1.5">Goodies, awards aur graduation moments — humare students ki real journey.</p>
          </div>
          <Link
            to="/wall-of-fame"
            className="hidden sm:inline-flex items-center gap-1.5 text-[#FF0066] hover:text-[#ff3380] text-sm font-semibold"
            data-testid="wof-marquee-viewall"
          >
            View all <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>

      <div className="relative overflow-hidden">
        <div className="flex gap-4 animate-wof-scroll" style={{ width: "max-content" }}>
          {loop.map((entry, idx) => (
            <Link
              key={`${entry.id}-${idx}`}
              to="/wall-of-fame"
              className="group relative w-[200px] sm:w-[240px] aspect-square shrink-0 rounded-xl overflow-hidden border border-white/10 hover:border-[#FF0066]/50 transition"
              data-testid={`wof-marquee-tile-${idx}`}
            >
              <img
                src={entry.image_url}
                alt={entry.caption || "Wall of fame"}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                loading="lazy"
                onError={(e) => { e.currentTarget.style.opacity = "0.3"; }}
              />
              {(entry.student_name || entry.caption) && (
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black via-black/70 to-transparent p-2.5">
                  {entry.student_name && <div className="text-xs font-semibold text-white truncate">{entry.student_name}</div>}
                  {entry.caption && <div className="text-[10px] text-white/70 line-clamp-2 mt-0.5">{entry.caption}</div>}
                </div>
              )}
            </Link>
          ))}
        </div>
      </div>

      <div className="sm:hidden text-center mt-5">
        <Link to="/wall-of-fame" className="inline-flex items-center gap-1.5 text-[#FF0066] text-sm font-semibold">
          View all <ArrowRight className="w-4 h-4" />
        </Link>
      </div>

      <style>{`
        @keyframes wof-scroll {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .animate-wof-scroll {
          animation: wof-scroll 40s linear infinite;
        }
        .animate-wof-scroll:hover {
          animation-play-state: paused;
        }
      `}</style>
    </section>
  );
}
